import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** 关闭动画回调世代：重开 / forceReset 时递增，避免旧回调误 reset */
  #closeGeneration = 0;

  /** 离页进入 bfcache 前先复位，缓存快照会是「已关闭」状态 */
  #onPageHide = () => {
    this.#forceReset();
  };

  /** 从 bfcache / 返回页恢复时再兜底一次 */
  #onPageShow = () => {
    this.#forceReset();
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onNavigateAway);
    this.#setupAnimatedElementListeners();
    this.#setupSubmenuToggleListeners();
    window.addEventListener('pagehide', this.#onPageHide);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onNavigateAway);
    window.removeEventListener('pagehide', this.#onPageHide);
    window.removeEventListener('pageshow', this.#onPageShow);
    removeTrapFocus();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * 菜单内真实跳转：立刻卸掉焦点陷阱并复位，避免未关抽屉就离页后返回卡死
   * @param {MouseEvent} event
   */
  #onNavigateAway = (event) => {
    if (!this.isOpen) return;
    if (event.defaultPrevented) return;
    if (event.button === 1 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute('href') || '';
    if (!href || href === '#' || href.startsWith('#') || href.startsWith('javascript:')) return;
    if (link.hasAttribute('aria-disabled')) return;
    // 新窗口 / 新浏览上下文：本页抽屉应保持，bfcache 不涉及
    if (link.target && link.target !== '_self' && link.target !== '') return;

    this.#forceReset();
  };

  /**
   * 无动画强制复位主抽屉与所有子 details，并释放 trapFocus / 滚动锁
   */
  #forceReset() {
    this.#closeGeneration += 1;
    removeTrapFocus();
    document.documentElement.removeAttribute('scroll-lock');
    const main = this.refs.details;
    if (!(main instanceof HTMLDetailsElement)) return;
    if (!main.open && !main.classList.contains('menu-open')) return;

    reset(main);
    main.querySelectorAll('details[open]').forEach((el) => {
      if (el instanceof HTMLDetailsElement) reset(el);
    });
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Prevent default behavior of details/summary
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainMenu = details === this.refs.details;
    
    // For submenus, let the browser's default behavior and toggle listener handle everything
    if (!isMainMenu) {
      return;
    }
    
    // 作废进行中的关闭动画回调，避免重开后被二次 reset
    this.#closeGeneration += 1;

    // Main menu: manually control the open state
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Get the actual animated element BEFORE removing classes
    const animatedElement = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    const generation = ++this.#closeGeneration;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the closing animation to complete
    onAnimationEnd(animatedElement || details, () => {
      // 已重开或强制复位：丢弃本次关闭收尾
      if (generation !== this.#closeGeneration) return;
      if (details.classList.contains('menu-open')) return;

      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          if (generation !== this.#closeGeneration) return;
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup listeners for submenu details toggle events
   * to sync menu-open class and aria-expanded with details open state
   */
  #setupSubmenuToggleListeners() {
    // Find all submenu details elements (not the main menu details)
    const submenuDetails = this.querySelectorAll('.menu-drawer__menu-container');
    
    submenuDetails.forEach((details) => {
      details.addEventListener('toggle', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLDetailsElement)) return;
        
        const summary = target.querySelector('summary');
        const isOpen = target.hasAttribute('open');
        
        // Sync menu-open class with details open state
        if (isOpen) {
          target.classList.add('menu-open');
          summary?.setAttribute('aria-expanded', 'true');
        } else {
          target.classList.remove('menu-open');
          summary?.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
